import React, { useCallback, useEffect, useState } from 'react';
import { Search, Download, Edit, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import adminApi from '../lib/adminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const RANGES = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

const LeadEditModal = ({ lead, onClose, onSaved }) => {
  const [form, setForm] = useState({
    visited: lead.visited || false,
    visit_date: lead.visit_date || '',
    sale_value: lead.sale_value || 0,
    notes: lead.notes || '',
    status: lead.status || 'New',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        visited: form.visited,
        visit_date: form.visit_date || null,
        sale_value: Number(form.sale_value) || 0,
        notes: form.notes,
        status: form.status,
      };
      const { data } = await adminApi.patch(`/leads/${lead.id}`, payload);
      toast.success('Lead updated');
      onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} data-testid="lead-edit-modal">
        <header className="admin-modal-header">
          <h3>Edit Lead</h3>
          <button onClick={onClose} aria-label="Close" data-testid="lead-edit-close"><X size={20} /></button>
        </header>

        <div className="admin-modal-body">
          <div className="admin-readonly">
            <div><strong>Name:</strong> {lead.name}</div>
            <div><strong>Phone:</strong> {lead.phone}</div>
            <div><strong>Created:</strong> {formatDate(lead.created_at)}</div>
          </div>

          <label>
            Status
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="lead-edit-status">
              <option value="New">New</option>
              <option value="Contacted">Contacted</option>
              <option value="Won">Won</option>
              <option value="Lost">Lost</option>
            </select>
          </label>

          <label>
            <input type="checkbox" checked={form.visited} onChange={(e) => setForm({ ...form, visited: e.target.checked })} data-testid="lead-edit-visited" />
            Visited the store
          </label>

          <label>
            Visit Date
            <input type="date" value={form.visit_date || ''} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} data-testid="lead-edit-visit-date" />
          </label>

          <label>
            Sale Value (₹)
            <input type="number" min="0" value={form.sale_value} onChange={(e) => setForm({ ...form, sale_value: e.target.value })} data-testid="lead-edit-sale" />
          </label>

          <label>
            Notes
            <textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="lead-edit-notes" />
          </label>
        </div>

        <footer className="admin-modal-footer">
          <button onClick={onClose} className="admin-btn-secondary">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="admin-btn-primary" data-testid="lead-edit-save">
            {saving ? <Loader2 className="spin" size={16} /> : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
};

const Leads = () => {
  const { user } = useAdminAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [range, setRange] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [editing, setEditing] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      params.set('range', range);
      if (range === 'custom') {
        if (!customStart || !customEnd) {
          setLoading(false);
          return;
        }
        params.set('start', customStart);
        params.set('end', customEnd);
      }
      const { data } = await adminApi.get(`/leads?${params}`);
      setLeads(data.items);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [search, range, customStart, customEnd]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleExport = async (fmt) => {
    try {
      const params = new URLSearchParams({ fmt, range });
      if (range === 'custom' && customStart && customEnd) {
        params.set('start', customStart);
        params.set('end', customEnd);
      }
      const resp = await adminApi.get(`/export/leads?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Leads</h1>
        <p className="admin-page-sub">Every form submission from o2nailshyd.com</p>
      </header>

      <div className="admin-toolbar">
        <div className="admin-search">
          <Search size={16} />
          <input
            placeholder="Search name or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="leads-search"
          />
        </div>

        <select value={range} onChange={(e) => setRange(e.target.value)} data-testid="leads-range">
          {RANGES.map((r) => (
            <option key={r.key} value={r.key}>{r.label}</option>
          ))}
        </select>

        {range === 'custom' && (
          <>
            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="leads-custom-start" />
            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="leads-custom-end" />
          </>
        )}

        <div className="admin-toolbar-right">
          <button onClick={() => handleExport('csv')} className="admin-btn-secondary" data-testid="export-csv">
            <Download size={14} /> CSV
          </button>
          <button onClick={() => handleExport('xlsx')} className="admin-btn-secondary" data-testid="export-xlsx">
            <Download size={14} /> Excel
          </button>
        </div>
      </div>

      <div className="admin-table-wrap">
        {loading ? (
          <div className="admin-loading">Loading…</div>
        ) : leads.length === 0 ? (
          <div className="admin-empty">No leads in this range.</div>
        ) : (
          <table className="admin-table" data-testid="leads-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Visited</th>
                <th>Visit Date</th>
                <th>Sale</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                  <td>{formatDate(l.created_at)}</td>
                  <td>{l.name}</td>
                  <td>{l.phone}</td>
                  <td><span className={`status-pill status-${l.status?.toLowerCase()}`}>{l.status || 'New'}</span></td>
                  <td>{l.visited ? '✓' : '—'}</td>
                  <td>{l.visit_date || '—'}</td>
                  <td>{l.sale_value ? inr(l.sale_value) : '—'}</td>
                  <td className="notes-col" title={l.notes}>{l.notes ? l.notes.slice(0, 40) + (l.notes.length > 40 ? '…' : '') : '—'}</td>
                  <td>
                    <button className="admin-icon-btn" onClick={() => setEditing(l)} data-testid={`lead-edit-btn-${l.id}`}>
                      <Edit size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <LeadEditModal
          lead={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
            setEditing(null);
          }}
        />
      )}

      {user?.role === 'manager' && (
        <p className="admin-hint">You can update Visited, Visit Date, Sale Value, Notes and Status. Deletion is restricted to Owner.</p>
      )}
    </div>
  );
};

export default Leads;
